import React, { useState, useEffect, useMemo } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  deleteService,
  getAllServices,
  getCategories,
  getStockTransaction,
} from "../../services/services";
import moment from "moment";
import { toast } from "react-toastify";
import { confirmDelete } from "../../components/confirmDelete";
import Loading from "../../components/loading";
import { useNavigate } from "react-router";
import Pagination from "../../components/Pagination";
import DataTable from "react-data-table-component";
import { X } from "lucide-react";
const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
  });

  const [categories, setCategories] = useState([]);
  const [totalServices, setTotalServices] = useState(0);
  const [itemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  const fetchServices = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        name: debouncedSearch || "",
        category: filters.category || "",
        page: currentPage,
        limit,
      }).toString();

      const res = await getAllServices(`?${params}`);

      if (res.data.status) {
        const data = res.data.response;

        setServices(data.services || []);
        setTotalServices(data.totalServices || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (err) {
      console.error("Failed to load services", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [debouncedSearch, filters.category, currentPage, limit]);

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        textTransform: "uppercase",
      },
    },
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500); // ⏱ 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [filters.search]);

  const loadCategories = async () => {
    const payload = {
      type: "service",
    };
    try {
      const res = await getCategories(payload);
      if (res.data.status) {
        setCategories(res.data.response);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id, name) => {
    const confirm = await confirmDelete(name);
    if (!confirm) return;
    try {
      await deleteService(id);
      toast.success("Service deleted");
      fetchServices();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleEdit = (service) => {
    navigate("/add-Services", { state: { editData: service } });
  };

  const handleAddNew = () => {
    navigate("/add-Services");
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  /* ========= DATATABLE COLUMNS ========= */
  const columns = useMemo(
    () => [
      {
        name: "S.No",
        width: "80px",
        cell: (_, index) => (currentPage - 1) * itemsPerPage + (index + 1),
      },
      {
        name: "Name",
        selector: (row) => row.name,
        sortable: true,
      },
      {
        name: "Service Code",
        cell: (row) => (
          <span style={{ whiteSpace: "nowrap" }}>{row.serviceCode || "-"}</span>
        ),
        // right: true,
      },

      {
        name: "SAC Code",
        selector: (row) => row.sacCode || "-",
      },
      {
        name: "Category",
        cell: (row) => row.category?.name || "-",
      },
      {
        name: "Price (₹)",
        cell: (row) => Number(row.baseCost || 0).toLocaleString("en-IN"),
      },
      {
        name: "GST Rate",
        cell: (row) => `${row.gstRate || 0}%`,
      },
      {
        name: "Created",
        cell: (row) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {moment(row.createdAt).format("DD MMM, YYYY")}
          </span>
        ),
      },
      {
        name: "Actions",
        width: "140px",
        cell: (row) => (
          <div style={{ whiteSpace: "nowrap" }}>
            <button
              className="btn btn-light btn-sm shadow-sm me-1 text-primary border"
              title="Edit Service"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
            >
              <i className="bi bi-pencil"></i>
            </button>

            <button
              className="btn btn-light btn-sm shadow-sm text-danger border"
              title="Delete Service"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row._id, row.name);
              }}
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [currentPage, itemsPerPage, categories],
  );

  return (
    <>
      <div id="main">
        <div className="d-flex flex-wrap flex-md-nowrap flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="custom-title mb-0">Services</h4>
            <div className="d-block d-md-none">
              <button className="btn btn-primary" onClick={handleAddNew}>
                Add Service
              </button>
            </div>
          </div>

          <div
            className="d-flex gap-2 align-items-center w-md-auto"
            style={{ maxWidth: "900px" }}
          >
            {/* Search input with clear button */}
            <div className="position-relative w-100">
              <input
                type="text"
                className="form-control pe-6"
                placeholder="Search by name..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, search: e.target.value }))
                }
                style={{ borderRadius: "8px" }}
              />

              {filters.search && (
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0 bg-transparent"
                  onClick={() => setFilters((f) => ({ ...f, search: "" }))}
                  aria-label="Clear search"
                >
                  <X size={16} className="text-muted" />
                </button>
              )}
            </div>

            {/* Category filter */}
            <select
              className="form-select"
              value={filters.category}
              onChange={(e) =>
                setFilters((f) => ({ ...f, category: e.target.value }))
              }
              style={{ borderRadius: "8px" }}
            >
              <option value="">All Categories</option>
              {categories?.length === 0 ? (
                <option value="" disabled>
                  No categories
                </option>
              ) : (
                categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))
              )}
            </select>

            {/* Add button */}
            <div className="d-none d-md-block text-nowrap">
              <button
                className="btn btn-primary"
                onClick={() => handleAddNew(true)}
              >
                Add Service
              </button>
            </div>
          </div>
        </div>
        <div
          className="font-semibold text-gray-700 mb-2"
          style={{ fontSize: "14px" }}
        >
          Total Services: {totalServices}
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body p-0 rounded">
            <DataTable
              columns={columns}
              data={services}
              progressPending={loading}
              customStyles={customStyles}
              progressComponent={<Loading />}
              noDataComponent={
                <div className="p-5 text-center text-muted">
                  <i className="bi bi-inbox display-1"></i>
                  <p className="mt-3">
                    {filters.search
                      ? "No services match your search"
                      : "No services found"}
                  </p>
                </div>
              }
              highlightOnHover
              striped
              onRowClicked={(row) => setSelected(row)}
              conditionalRowStyles={[
                {
                  when: (row) => selected?._id === row._id,
                  style: { backgroundColor: "#e7f1ff" },
                },
              ]}
            />
          </div>
        </div>
        {services.length > 0 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            limit={limit}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        )}
      </div>

      {/* {showSidebar && selectedProduct && (
        <div className="overlay" onClick={handleCloseSidebar}>
          <div className="bg-light" onClick={(e) => e.stopPropagation()}>
            <StockTransactions
              transactions={transactions}
              isLoading={isLoadingItems}
              selectedProduct={selectedProduct}
              selectedTransactionId={null}
              onSelectTransaction={setTransactions}
              onClose={handleCloseSidebar}
              // onSuccess={handleSuccess}
            />
          </div>
        </div>
      )} */}

      {/* Modal — Add + Edit */}
      {/* {showAddModal && (
        <AddService
          onClose={() => {
            setShowAddModal(false);
            setEditData(null);
          }}
          fetchServices={fetchServices}
          editData={editData}
        />
      )} */}
    </>
  );
};

export default headerLayoutWrapper(Services);
